import type { FetchResult, Operation } from '@apollo/client/core/index.js'
import { ApolloLink, Observable } from '@apollo/client/core/index.js'
import { getOperationDefinition } from '@apollo/client/utilities/index.js'
import { context, SpanKind, SpanStatusCode, type Tracer, trace } from '@mntn/observability'
import { OperationTypeNode } from 'graphql'

const getOperationMeta = (operation: Operation) => {
  const def = getOperationDefinition(operation.query)

  const operationType = def?.operation ?? OperationTypeNode.QUERY

  const operationName = operation.operationName ?? def?.name?.value ?? 'anonymous'

  return { operationType, operationName }
}

/**
 * Apollo link that creates a **child span per GraphQL call** and ensures that
 * downstream HTTP request spans (from Apollo's transport) are **children** of
 * this GraphQL span, which itself is a child of the **original HTTP request span**.
 *
 * How parent/child relationships are preserved with OpenTelemetry:
 * 1. We get the active span from the current context (eg. `http.server` span
 *    created by OpenTelemetry's HttpInstrumentation).
 * 2. We create a child `graphql.request` span (automatically inherits parent from context).
 * 3. We run the observable work inside `context.with()`, activating the span so any
 *    auto-instrumented `http.request` spans created by Apollo's network
 *    layer become **children of this GraphQL span**.
 */
export const createTracerLink = ({ tracer, uri }: { tracer: Tracer; uri: string }): ApolloLink => {
  return new ApolloLink((operation, forward) => {
    const { operationType, operationName } = getOperationMeta(operation)

    const spanName = `${operationType} ${operationName}`

    /** Create a child span for this specific graphql call */
    const span = tracer.startSpan(spanName, {
      kind: SpanKind.CLIENT,
      attributes: {
        'graphql.operation.type': operationType,
        'graphql.operation.name': operationName,
        'graphql.resource.name': `${operationType} ${operationName}`,
        'http.url': uri,
        component: 'apollo-client',
      },
    })

    return new Observable<FetchResult>(observer => {
      let spanFinished = false
      const finish = () => {
        if (!spanFinished) {
          spanFinished = true
          span.end()
        }
      }

      /** Activate this span so downstream HTTP requests become its children */
      const activeContext = trace.setSpan(context.active(), span)

      const sub = context.with(activeContext, () => {
        return forward(operation).subscribe({
          next: result => {
            if (result.errors?.length) {
              const first = result.errors[0]
              const error = first instanceof Error ? first : new Error(first?.message || 'GraphQL Error')

              span.recordException(error)
              span.setStatus({
                code: SpanStatusCode.ERROR,
                message: error.message,
              })
              span.setAttribute('graphql.errors.count', result.errors.length)
            }
            observer.next(result)
          },
          error: err => {
            const error = err instanceof Error ? err : new Error(String(err))
            span.recordException(error)
            span.setStatus({
              code: SpanStatusCode.ERROR,
              message: error.message,
            })
            finish()
            observer.error(err)
          },
          complete: () => {
            if (span.isRecording()) {
              span.setStatus({ code: SpanStatusCode.OK })
            }
            finish()
            observer.complete()
          },
        })
      })

      return () => {
        finish()
        sub.unsubscribe()
      }
    })
  })
}
